import React from 'react';
import './styles.css';
import { Card } from '../Card';

interface Props {
    renderCard?: boolean;
}

const Rules: React.FC<Props> = ({ renderCard = true }) => {
    const content = (
        <div className="rules">
            <h1>Reglas rápidas</h1>
            <h2>Hoja de personaje</h2>
            <p>Cada personaje cuenta con:</p>
            <ul>
                <li>Nombre, Profesión y una <strong>descripción breve</strong>.</li>
                <li><strong>Siete características</strong>, con valores: 1, 1, 2, 2, 2, 3, 3.</li>
            </ul>

            <p><strong>Características:</strong></p>
            <ul>
                <li><strong>Agilidad</strong>: Reflejos, equilibrio, movimiento rápido.</li>
                <li><strong>Fortaleza</strong>: Resistencia física y capacidad de esfuerzo.</li>
                <li><strong>Destreza</strong>: Precisión manual y control fino.</li>
                <li><strong>Cultura</strong>: Conocimiento y deducción basada en información.</li>
                <li><strong>Oficio</strong>: Habilidades propias de la ocupación principal del personaje.</li>
                <li><strong>Templanza</strong>: Autocontrol, fortaleza mental y emocional.</li>
                <li><strong>Perspicacia</strong>: Detección de detalles ocultos en objetos o personas.</li>
            </ul>

            <h2>Resolución de acciones</h2>
            <p>Las acciones se resuelven con una tirada de <strong>dados de 6 caras</strong>, según el valor de la característica usada, más los puntos de Brío o Estrés empleados.</p>
            <ul>
                <li>Las <strong>acciones fáciles</strong> no requieren tirada.</li>
                <li>La dificultad puede ser de <strong>4</strong> (moderada), <strong>5</strong> (difícil) o <strong>6</strong> (muy difícil).</li>
                <li>Un resultado igual o superior al valor de la dificultad en un dado cuenta como <strong>éxito</strong>.</li>
                <li>Los dados <em>explotan</em>: cada <strong>6</strong> cuenta como éxito y además permite <strong>tirar un dado adicional</strong>.</li>
            </ul>

            <p><strong>Escala de resultados:</strong></p>
            <ul>
                <li><strong>0 éxitos</strong>: Fracaso total. No se logra el objetivo y puede haber consecuencias.</li>
                <li><strong>1 éxito</strong>: Éxito parcial o con complicaciones.</li>
                <li><strong>2 éxitos</strong>: Éxito completo, sin problemas.</li>
                <li><strong>3 éxitos</strong>: Éxito sobresaliente, con beneficio adicional.</li>
            </ul>

            <p><strong>Ejemplo:</strong> Un personaje con Destreza 3 intenta forzar una cerradura (dificultad 4). Lanza 3d6 y obtiene 4, 2, 5: consigue dos éxitos y abre la cerradura sin dificultades.</p>

            <h2>Brío y Estrés</h2>
            <ul>
                <li>El <strong>Brío</strong> representa la energía del personaje. El Brío se gasta. Gastando un punto (antes de la tirada) permite añadir un dado al total a tirar.</li>
                <li>Al agotarlo, puede usarse <strong>estrés</strong> para seguir añadiendo un dado a las tiradas. El estrés se acumula, un punto añadido permite tirar un dado adicional.</li>
                <li>Máximo de <strong>3 puntos</strong> por tirada (Brío y/o estrés).</li>
                <li>Los personajes comienzan con <strong>3 puntos de Brío</strong>.</li>
                <li>Tres horas de descanso permiten recuperar un punto de Brío o Estrés. Una noche de descanso permite recuperar hasta tres puntos de Brío o Estrés.</li>
            </ul>

            <h3>Chequeos de resistencia por estrés:</h3>
            <div style={{ overflow: 'auto', maxWidth: '100%' }}>
                <table>
                    <thead>
                    <tr>
                        <th>Estrés acumulado</th>
                        <th>Dificultad</th>
                        <th>0 éxitos</th>
                        <th>1 éxito</th>
                        <th>2+ éxitos</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td>0–3</td>
                        <td>4+</td>
                        <td>Consecuencia leve</td>
                        <td>Sin consecuencias</td>
                        <td>Sin consecuencias</td>
                    </tr>
                    <tr>
                        <td>4–6</td>
                        <td>5+</td>
                        <td>Consecuencia moderada</td>
                        <td>Consecuencia leve</td>
                        <td>Sin consecuencias</td>
                    </tr>
                    <tr>
                        <td>7+</td>
                        <td>6+</td>
                        <td>Consecuencia grave</td>
                        <td>Consecuencia moderada</td>
                        <td>Consecuencia leve / Sin consecuencias (3 éxitos)</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <p><strong>Consecuencias por estrés:</strong></p>
            <ul>
                <li><strong>Leve</strong>: –1 dado en acciones físicas o mentales durante una hora.</li>
                <li><strong>Moderada</strong>: –1 dado hasta descansar o recuperarse.</li>
                <li><strong>Grave</strong>: –2 dados hasta tratar el problema.</li>
                <li><strong>Crítica</strong>: El personaje queda incapacitado.</li>
            </ul>

            <p>Las consecuencias se describen narrativamente (ej.: <em>Herido, Nervioso, Cojera…</em>).</p>

            <h2>Puntos de Sombra</h2>
            <ul>
                <li>Su efecto depende de la ambientación.</li>
                <li>Se ganan al vivir situaciones concretas (p. ej., corrupción, horrores, traumas).</li>
                <li>Representan cambios en la moralidad o personalidad del personaje.</li>
                <li>Pueden requerir chequeos de resistencia.</li>
                <li>Reducibles, pero no de forma trivial.</li>
            </ul>
        </div>
    );

    if (!renderCard) {
        return content;
    }

    return <Card size='large'>{content}</Card>;
};

export default Rules;
